<template>
  <div
    :class="classObj"
    class="app-wrapper mixed-layout"
    :style="{ '--current-color': theme, '--sidebar-width': sidebarWidth, '--brand-width': brandWidth }"
  >
    <div
      v-if="hasSidebar && device === 'mobile' && sidebar.opened"
      class="drawer-bg"
      @click="handleClickOutside"
    />
    <navbar class="mixed-navbar" @setLayout="setLayout" />
    <sidebar v-if="hasSidebar" class="sidebar-container" />
    <button v-if="hasSidebar && device === 'mobile' && !sidebar.opened" type="button"
      class="mobile-sidebar-open" :aria-label="$tt('展开导航')" :title="$tt('展开导航')"
      @click="useAppStore().toggleSideBar()">
      <el-icon aria-hidden="true"><Expand /></el-icon>
    </button>
    <div
      :class="{ sidebarHide: !hasSidebar, withoutBreadcrumb: !showBreadcrumb, 'teacher-detail-layout': $route.path === '/teachers-and-students/teachers/detail' }"
      class="main-container"
    >
      <div :class="{ 'fixed-header': fixedHeader }">
        <div v-if="showBreadcrumb" class="mixed-page-location"><breadcrumb /></div>
        <!-- Keep route/cache bookkeeping without displaying the visited-page strip. -->
        <tags-view v-show="false" />
      </div>
      <app-main />
      <settings ref="settingRef" />
    </div>
  </div>
</template>

<script setup>
import { useWindowSize } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import Sidebar from "./components/Sidebar/index.vue";
import { Expand } from "@element-plus/icons-vue";
import { AppMain, Navbar, Settings, TagsView } from "./components";
import Breadcrumb from "@/components/Breadcrumb/index.vue";
import { usePageBreadcrumb } from "@/composables/usePageBreadcrumb";
import { useMixedNavigation } from "@/composables/useMixedNavigation";
const { hasSidebar } = useMixedNavigation();

import useAppStore from "@/store/modules/app";
import useSettingsStore from "@/store/modules/settings";

const settingsStore = useSettingsStore();
const { locale } = useI18n();
const { showBreadcrumb } = usePageBreadcrumb();
const theme = computed(() => settingsStore.theme);
const sideTheme = computed(() => settingsStore.sideTheme);
const sidebar = computed(() => useAppStore().sidebar);
const device = computed(() => useAppStore().device);
const fixedHeader = computed(() => settingsStore.fixedHeader);
const sidebarWidth = computed(() => locale.value === "en-US" ? "240px" : "180px");
const brandWidth = computed(() => locale.value === "en-US" ? "260px" : "200px");

const classObj = computed(() => ({
  hideSidebar: !sidebar.value.opened,
  openSidebar: sidebar.value.opened,
  withoutAnimation: sidebar.value.withoutAnimation,
  mobile: device.value === "mobile",
}));

const { width, height } = useWindowSize();
const WIDTH = 992; // refer to Bootstrap's responsive design

watch(
  () => device.value,
  () => {
    if (device.value === "mobile" && sidebar.value.opened) {
      useAppStore().closeSideBar({ withoutAnimation: false });
    }
  }
);

watchEffect(() => {
  if (width.value - 1 < WIDTH) {
    useAppStore().toggleDevice("mobile");
    useAppStore().closeSideBar({ withoutAnimation: true });
  } else {
    useAppStore().toggleDevice("desktop");
  }
});

function handleClickOutside() {
  useAppStore().closeSideBar({ withoutAnimation: false });
}

const settingRef = ref(null);
function setLayout() {
  settingRef.value.openSetting();
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixin.scss";
@import "@/assets/styles/variables.module.scss";

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  width: calc(100% - var(--sidebar-width));
  transition: width 0.28s;
}

.hideSidebar .fixed-header {
  width: calc(100% - 64px);
}

.sidebarHide .fixed-header {
  width: 100%;
}

.mobile .fixed-header {
  width: 100%;
}
</style>
